import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Pagination } from './ui/pagination';
import { storeApiService, StoreTransaction } from '../services/storeApi';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';

function formatDateTime(value: string | undefined): string {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function StoreItemIssueHistoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();

  const params = useParams<{ storeId: string; itemId: string }>();
  const storeId = Number(params.storeId);
  const itemId = Number(params.itemId);

  const itemNameFromState = (location.state as any)?.itemName as string | undefined;
  const storeNameFromState = (location.state as any)?.storeName as string | undefined;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<StoreTransaction[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const branchId = selectedBranch ?? user?.defaultBranchId;

  const load = async () => {
    if (!storeId || !itemId) return;
    try {
      setLoading(true);
      // We scope by storeId + itemId. Store is already within a branch context.
      const res = await storeApiService.getStoreTransactions(
        storeId,
        itemId,
        currentPage,
        pageSize,
        debouncedSearch || undefined,
        undefined
      );

      const data = Array.isArray((res as any).data) ? ((res as any).data as StoreTransaction[]) : [];
      setRows(data);

      const pages =
        Number((res as any)?.pagination?.totalPages) ||
        Math.max(1, Math.ceil((Number((res as any)?.pagination?.totalCount) || data.length) / pageSize));
      setTotalPages(pages);
    } catch (e) {
      toast.error('Failed to load issue history');
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, itemId, currentPage, pageSize, debouncedSearch, branchId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const title = useMemo(() => {
    const parts = [];
    if (storeNameFromState) parts.push(storeNameFromState);
    if (itemNameFromState) parts.push(itemNameFromState);
    return parts.join(' • ') || 'Item Issue History';
  }, [itemNameFromState, storeNameFromState]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" className="border-primary-200 text-primary-700 hover:bg-primary-50" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
              <p className="text-sm text-gray-600">
                Store ID: {storeId} • Item ID: {itemId}
              </p>
            </div>
          </div>

          <Button
            onClick={load}
            variant="outline"
            className="border-primary-200 text-primary-700 hover:bg-primary-50"
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>

        <Card className="border-primary-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-lg">Transactions</CardTitle>
              <div className="w-full max-w-sm">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by ref/reason/notes..."
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-white"
                />
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
                <p className="text-gray-500">Loading history...</p>
              </div>
            ) : rows.length === 0 ? (
              <div className="p-10 text-center text-gray-500">No transactions found.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead className="font-semibold text-gray-800">Date</TableHead>
                      <TableHead className="font-semibold text-gray-800">Type</TableHead>
                      <TableHead className="font-semibold text-gray-800 text-right">Qty</TableHead>
                      <TableHead className="font-semibold text-gray-800">Unit</TableHead>
                      <TableHead className="font-semibold text-gray-800">Reference</TableHead>
                      <TableHead className="font-semibold text-gray-800">Reason</TableHead>
                      <TableHead className="font-semibold text-gray-800">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id} className="hover:bg-gray-50/50">
                        <TableCell className="text-gray-900 whitespace-nowrap">
                          {formatDateTime(r.transactionDate || r.createdAt)}
                        </TableCell>
                        <TableCell className="text-gray-700">{r.transactionType}</TableCell>
                        <TableCell className="text-right font-semibold text-primary-700">
                          {(r.quantity ?? 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-gray-700">{r.unitOfMeasure || 'N/A'}</TableCell>
                        <TableCell className="text-gray-700">{r.referenceType || 'N/A'}</TableCell>
                        <TableCell className="text-gray-700">{r.reason || '—'}</TableCell>
                        <TableCell className="text-gray-700">{r.notes || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="p-4 flex items-center justify-end">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

